/**
 * fileHash — client-side SHA-256 of a base64 payload for /v3/documents/upload-attached.
 *
 * Phase H.1c (2026-05-01). The server recomputes the digest from the bytes it
 * actually received and rejects on mismatch — so this client digest is
 * "best-effort + tamper signal", not the authoritative hash. We send it on
 * the request so the server can short-circuit obvious base64 corruption
 * without having to trust the size header.
 *
 * Uses the WebCrypto API (`crypto.subtle.digest`). Available in Chromium /
 * Edge / Teams desktop without a polyfill. Returns a lowercase hex string.
 *
 * If the runtime doesn't expose `crypto.subtle` (very old environments) we
 * return an empty string and let the caller decide whether to skip the
 * field — the server treats the absent header as "not provided".
 */

/**
 * Decode a base64 string to a Uint8Array. We avoid `Buffer` (Node-only) and
 * stick to atob + Uint8Array for browser compatibility.
 */
function base64ToBytes(base64: string): Uint8Array {
  // atob may throw on malformed base64; let the caller handle it.
  const binary = atob(base64);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    out[i] = binary.charCodeAt(i);
  }
  return out;
}

/**
 * Compute the SHA-256 of the bytes encoded in `base64`. Returns a lowercase
 * hex string (64 chars). Returns '' if SubtleCrypto is unavailable or the
 * base64 payload can't be decoded — never throws.
 */
export async function sha256OfBase64(base64: string): Promise<string> {
  if (!base64) return '';
  if (typeof crypto === 'undefined' || !crypto.subtle) return '';
  try {
    const bytes = base64ToBytes(base64);
    const buf = await crypto.subtle.digest(
      'SHA-256',
      bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer,
    );
    const view = new Uint8Array(buf);
    let hex = '';
    for (let i = 0; i < view.length; i++) {
      const b = view[i] ?? 0;
      hex += b.toString(16).padStart(2, '0');
    }
    return hex;
  } catch {
    return '';
  }
}
